import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { CaseSummary, ConversationSummary, Locale } from "../../../shared/types";
import type {
  CreatedInvite,
  Respondent,
  StudyAudience,
  StudySummary,
  TopicDraft,
} from "../lib/api";
import * as api from "../lib/api";
import { acquireBlob, type BlobRenderer } from "../blob/renderer";
import { formatSince, useCopy } from "../lib/copy";
import Cases from "./Cases";
import StudyBoard from "./StudyBoard";

/**
 * The researcher's side of a study: the list, the making of one, and the board.
 *
 * This is a SECOND surface, not a mode of the first. The stage, the blob, the dock
 * and the notes rail are untouched — a study is what you do when the consultant says
 * "you should ask five customers", and the conversation is still there when you come
 * back. The two share the top bar and the one status line and nothing else.
 *
 * Everything here obeys the same rules the conversation does: one accent, no toasts,
 * no spinners, the blob is the only loader, and every count is "7 of 11" because a
 * percentage of nine people is a lie.
 */

export type Tone = "quiet" | "warn" | "error";

/**
 * The two channels a study screen has for saying anything, handed down rather than
 * duplicated, because there is exactly one status line and one blob.
 *
 * `setBusy` is a LIVE line: it holds for as long as the thing is happening and it is
 * what puts the blob into `thinking`. `say` is transient and expires. Live outranks
 * transient in the status line, for the reason `Stage` learned the hard way: a stale
 * complaint that outlives its moment makes the page read as frozen.
 */
export interface StudyShell {
  setBusy: (text: string | null) => void;
  say: (text: string, tone?: Tone) => void;
}

interface StudiesProps {
  locale: Locale;
  theme: "dark" | "light";
  /** The business these studies belong to. Studies are scoped to a case, always. */
  caseId: number;
  cases: CaseSummary[];
  conversations: ConversationSummary[];
  activeConversationId: number | null;
  respondent: Respondent | null;
  /** Which study is open, or null for the list. Owned by App so a reload is honest. */
  studyId: number | null;
  onOpenStudy: (id: number | null) => void;
  /** Out of studies and back to talking. */
  onBack: () => void;
  onSwitchCase: (id: number) => void;
  onCreateCase: (name: string) => void;
  onRenameCase: (id: number, name: string) => void;
  onOpenConversation: (id: number) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: number) => void;
  onRedoIntake: () => void;
  onUploadDocument: (file: File) => void;
  onToggleTheme: () => void;
  onChangeLocale: (locale: Locale) => void;
  onSignOut: () => void;
}

/** As long as anything the page is told out of band holds the status line. */
const NOTICE_MS = 6000;

/** A status line long enough to be a sentence is set as one, not as an 11px label. */
const LABEL_MAX_CHARS = 22;

/** The offered time budgets. 12 is the default because abandonment climbs past it. */
const MINUTES = [12, 15, 25, 40];

/** Three empty rungs, which is the floor the spine is allowed to have. */
const EMPTY_SPINE: TopicDraft[] = [
  { title: "", intent: "" },
  { title: "", intent: "" },
  { title: "", intent: "" },
];

const AUDIENCES: StudyAudience[] = ["customer", "employee", "friendly"];

/** Endonyms, as everywhere else: the other language's own name for itself. */
const OTHER_LANGUAGE: Record<Locale, string> = { en: "Русский", ru: "English" };

export default function Studies({
  locale,
  theme,
  caseId,
  cases,
  conversations,
  activeConversationId,
  respondent,
  studyId,
  onOpenStudy,
  onBack,
  onSwitchCase,
  onCreateCase,
  onRenameCase,
  onOpenConversation,
  onNewConversation,
  onDeleteConversation,
  onRedoIntake,
  onUploadDocument,
  onToggleTheme,
  onChangeLocale,
  onSignOut,
}: StudiesProps) {
  const copy = useCopy(locale);

  const [studies, setStudies] = useState<StudySummary[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; tone: Tone } | null>(null);
  /**
   * The link that has just been minted. It is handed to the board once and then
   * dropped: the raw token is never stored, so this object is the only copy that
   * exists anywhere the researcher can read it.
   */
  const [freshInvite, setFreshInvite] = useState<CreatedInvite | null>(null);

  const blobRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<BlobRenderer | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (notice === null) return;
    const timer = window.setTimeout(() => setNotice(null), NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const say = useCallback((text: string, tone: Tone = "quiet"): void => {
    setNotice({ text, tone });
  }, []);

  const shell = useMemo<StudyShell>(() => ({ setBusy, say }), [say]);

  // Borrowed, not built: the same shared canvas the conversation uses, parked in the
  // top bar's docking slot. On this screen the blob has nowhere to be but docked, and
  // it is still the only thing that says a run is under way.
  useEffect(() => {
    const host = blobRef.current;
    if (!host) return;
    const { canvas, renderer } = acquireBlob();
    host.appendChild(canvas);
    rendererRef.current = renderer;
    renderer.start();
    return () => {
      rendererRef.current = null;
      renderer.stop();
      renderer.setLevel(0);
      canvas.remove();
    };
  }, []);

  useEffect(() => {
    rendererRef.current?.setState(busy === null ? "idle" : "thinking");
  }, [busy]);

  // The case's studies. Reloaded when the business on the table changes, because a
  // study belongs to a case and showing one case's studies under another's name is
  // the exact bug the switcher exists to prevent.
  useEffect(() => {
    setStudies(null);
    const controller = new AbortController();
    setBusy(copy.thinking);
    void (async () => {
      try {
        const list = await api.listStudies(caseId, controller.signal);
        if (!controller.signal.aborted) setStudies(list);
      } catch (err) {
        if (controller.signal.aborted) return;
        setStudies([]);
        say(api.describe(err, copy.studiesListFailed), "error");
      } finally {
        if (!controller.signal.aborted) setBusy(null);
      }
    })();
    return () => controller.abort();
  }, [caseId, copy.studiesListFailed, copy.thinking, say]);

  /** Keeps the list honest after the board renames or closes something. */
  const noteChange = useCallback((study: StudySummary): void => {
    setStudies((current) =>
      current === null ? current : current.map((item) => (item.id === study.id ? study : item)),
    );
  }, []);

  const openList = useCallback((): void => {
    setFreshInvite(null);
    onOpenStudy(null);
  }, [onOpenStudy]);

  const filePicked = useCallback(
    (input: HTMLInputElement): void => {
      const file = input.files?.[0];
      input.value = "";
      if (file !== undefined) onUploadDocument(file);
    },
    [onUploadDocument],
  );

  const status = busy !== null ? { text: busy, tone: "quiet" as Tone } : notice;
  const statusClass = [
    "status",
    status === null || status.tone === "quiet" ? "" : `status--${status.tone}`,
    status !== null && status.text.length > LABEL_MAX_CHARS ? "status--sentence" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="field field--studies">
      <h1 className="sr-only">{copy.studiesLabel}</h1>

      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.txt,.md,.csv"
        style={{ display: "none" }}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => filePicked(event.currentTarget)}
      />

      <header className="topbar">
        {/* The blob's slot, and on this screen its permanent home. It is docked from
            the start, so nothing about it moves and nothing has to be measured. */}
        <div className="topbar__slot studies__blob" aria-hidden="true" ref={blobRef} />
        <Cases
          locale={locale}
          cases={cases}
          conversations={conversations}
          activeCaseId={caseId}
          activeConversationId={activeConversationId}
          respondent={respondent}
          onSwitch={onSwitchCase}
          onCreate={onCreateCase}
          onRename={onRenameCase}
          onOpenConversation={onOpenConversation}
          onNewConversation={onNewConversation}
          onDeleteConversation={onDeleteConversation}
          onRedoIntake={onRedoIntake}
          onOpenStudies={openList}
          studyCount={studies === null ? null : studies.length}
          onAttach={() => fileRef.current?.click()}
          theme={theme}
          onToggleTheme={onToggleTheme}
          onChangeLocale={onChangeLocale}
          onSignOut={onSignOut}
        />
        <div className="topbar__spacer" />
        <div className="topbar__chrome">
          <button className="btn btn--text" type="button" onClick={onToggleTheme}>
            {theme === "dark" ? copy.themeToLight : copy.themeToDark}
          </button>
          <button
            className="btn btn--text"
            type="button"
            onClick={() => onChangeLocale(locale === "en" ? "ru" : "en")}
          >
            {OTHER_LANGUAGE[locale]}
          </button>
          <button className="btn btn--text" type="button" onClick={onSignOut}>
            {copy.signOutCta}
          </button>
        </div>
      </header>

      <main className="studies">
        {/* The way back, in the content rather than in the bar: below 768px the
            bar's three text controls are hidden, and this one may never be. */}
        <button className="studies__leave label" type="button" onClick={onBack}>
          {copy.studiesBack}
        </button>

        {studyId !== null ? (
          <StudyBoard
            key={studyId}
            locale={locale}
            studyId={studyId}
            shell={shell}
            freshInvite={freshInvite}
            onInviteDropped={() => setFreshInvite(null)}
            onBack={openList}
            onChanged={noteChange}
          />
        ) : creating ? (
          <CreateStudy
            locale={locale}
            caseId={caseId}
            shell={shell}
            onCancel={() => setCreating(false)}
            onCreated={(study, invite) => {
              setStudies((current) => (current === null ? [study] : [study, ...current]));
              setFreshInvite(invite);
              setCreating(false);
              onOpenStudy(study.id);
            }}
          />
        ) : (
          <StudyList
            locale={locale}
            studies={studies}
            onOpen={onOpenStudy}
            onNew={() => setCreating(true)}
          />
        )}
      </main>

      {/* The one place state is written on this screen too. No toasts, ever. */}
      <p className={statusClass} role="status" aria-live="polite">
        {status?.text ?? ""}
      </p>
    </div>
  );
}

// --- the list --------------------------------------------------------------

interface StudyListProps {
  locale: Locale;
  /** Null while it is still being read. The blob carries that, not a skeleton. */
  studies: StudySummary[] | null;
  onOpen: (id: number) => void;
  onNew: () => void;
}

function StudyList({ locale, studies, onOpen, onNew }: StudyListProps) {
  const copy = useCopy(locale);
  return (
    <>
      <h2 className="studies__title">{copy.studiesTitle}</h2>
      <p className="studies__sub">{copy.studiesSub}</p>

      <div className="studies__act">
        <button className="btn btn--primary" type="button" onClick={onNew}>
          {copy.studyNewCta}
        </button>
      </div>

      {studies !== null && studies.length === 0 && (
        <p className="studies__empty">{copy.studiesEmpty}</p>
      )}

      {studies !== null && studies.length > 0 && (
        <ul className="study-list">
          {studies.map((study) => (
            <li key={study.id}>
              <button className="study-card" type="button" onClick={() => onOpen(study.id)}>
                <span className="study-card__meta label">
                  {copy.studyStatus[study.status]}
                  <span aria-hidden="true"> · </span>
                  {copy.studyAudience[study.audience]}
                  <span aria-hidden="true"> · </span>
                  {formatSince(study.updatedAt, locale)}
                </span>
                <span className="study-card__goal">{study.goal}</span>
                <span className="study-card__count num">
                  {copy.ofN(study.completedCount, study.targetN)}
                  <span className="study-card__count-sub"> {copy.studyProgressSub}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// --- making one ------------------------------------------------------------

interface CreateStudyProps {
  locale: Locale;
  caseId: number;
  shell: StudyShell;
  onCancel: () => void;
  onCreated: (study: StudySummary, invite: CreatedInvite | null) => void;
}

/**
 * From "I have a question" to "here is a link I can send", on one screen.
 *
 * Not a wizard. Four decisions and a spine, all visible at once, all with a sane
 * default, so the only field anyone HAS to fill in is the question itself. The spine
 * can be drafted from that question with one light call — the researcher edits rather
 * than authors, which is the difference between two minutes and twenty.
 */
function CreateStudy({ locale, caseId, shell, onCancel, onCreated }: CreateStudyProps) {
  const copy = useCopy(locale);
  const [goal, setGoal] = useState("");
  const [audience, setAudience] = useState<StudyAudience>("customer");
  const [minutes, setMinutes] = useState(12);
  const [targetN, setTargetN] = useState(8);
  const [topics, setTopics] = useState<TopicDraft[]>(EMPTY_SPINE);
  const [hypotheses, setHypotheses] = useState<string[]>([""]);
  const [working, setWorking] = useState(false);
  const goalRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    goalRef.current?.focus();
  }, []);

  const setTopic = (index: number, patch: Partial<TopicDraft>): void => {
    setTopics((current) =>
      current.map((topic, i) => (i === index ? { ...topic, ...patch } : topic)),
    );
  };

  const draft = (): void => {
    if (goal.trim().length === 0) {
      shell.say(copy.studyGoalRequired, "warn");
      goalRef.current?.focus();
      return;
    }
    setWorking(true);
    shell.setBusy(copy.studySpineDrafting);
    void (async () => {
      try {
        const drafted = await api.draftStudy(caseId, {
          goal: goal.trim(),
          audience,
          timeBudgetMin: minutes,
          language: locale,
        });
        if (drafted.guide.length > 0) setTopics(drafted.guide);
        // Only fills beliefs the researcher has not written for themselves.
        setHypotheses((current) =>
          current.every((item) => item.trim().length === 0) && drafted.hypotheses.length > 0
            ? drafted.hypotheses
            : current,
        );
      } catch (err) {
        shell.say(api.describe(err, copy.studySpineDraftFailed), "error");
      } finally {
        setWorking(false);
        shell.setBusy(null);
      }
    })();
  };

  const create = (): void => {
    if (goal.trim().length === 0) {
      shell.say(copy.studyGoalRequired, "warn");
      goalRef.current?.focus();
      return;
    }
    setWorking(true);
    shell.setBusy(copy.studyCreating);
    void (async () => {
      try {
        const study = await api.createStudy(caseId, {
          goal: goal.trim(),
          audience,
          targetN,
          timeBudgetMin: minutes,
          languages: [locale],
          guide: topics
            .map((topic) => ({ title: topic.title.trim(), intent: topic.intent.trim() }))
            .filter((topic) => topic.title.length > 0),
          hypotheses: hypotheses.map((item) => item.trim()).filter((item) => item.length > 0),
        });
        // The link is the point of the screen, so it is minted here rather than made
        // a second errand. A study without one is still a study, so a failure to mint
        // is said out loud and the board opens anyway.
        let invite: CreatedInvite | null = null;
        try {
          invite = await api.createInvite(study.id, { kind: "open", maxUses: study.targetN });
        } catch (err) {
          shell.say(api.describe(err, copy.studyLinkFailed), "error");
        }
        onCreated(study, invite);
      } catch (err) {
        shell.say(api.describe(err, copy.studyCreateFailed), "error");
      } finally {
        setWorking(false);
        shell.setBusy(null);
      }
    })();
  };

  return (
    <form
      className="make"
      onSubmit={(event) => {
        event.preventDefault();
        create();
      }}
    >
      <h2 className="studies__title">{copy.studyCreateTitle}</h2>
      <p className="studies__sub">{copy.studyCreateSub}</p>

      <div className="make__block">
        <label className="make__label label" htmlFor="study-goal">
          {copy.studyGoalLabel}
        </label>
        <textarea
          id="study-goal"
          ref={goalRef}
          className="input make__goal"
          rows={2}
          value={goal}
          placeholder={copy.studyGoalPlaceholder}
          onChange={(event) => setGoal(event.target.value)}
        />
        <p className="make__hint">{copy.studyGoalHint}</p>
      </div>

      <div className="make__block">
        <span className="make__label label" id="study-audience">
          {copy.studyAudienceLabel}
        </span>
        <div className="make__choices" role="group" aria-labelledby="study-audience">
          {AUDIENCES.map((option) => (
            <button
              key={option}
              className="make__choice"
              type="button"
              aria-pressed={audience === option}
              onClick={() => setAudience(option)}
            >
              {copy.studyAudience[option]}
            </button>
          ))}
        </div>
        <p className="make__hint">{copy.studyAudienceHint[audience]}</p>
      </div>

      <div className="make__row">
        <div className="make__block">
          <span className="make__label label" id="study-time">
            {copy.studyTimeLabel}
          </span>
          <div className="make__choices" role="group" aria-labelledby="study-time">
            {MINUTES.map((option) => (
              <button
                key={option}
                className="make__choice num"
                type="button"
                aria-pressed={minutes === option}
                onClick={() => setMinutes(option)}
              >
                {copy.studyMinutes(option)}
              </button>
            ))}
          </div>
          <p className="make__hint">{copy.studyTimeHint}</p>
        </div>

        <div className="make__block make__block--narrow">
          <label className="make__label label" htmlFor="study-target">
            {copy.studyTargetLabel}
          </label>
          <input
            id="study-target"
            className="input make__number num"
            type="number"
            min={1}
            max={200}
            value={targetN}
            onChange={(event) => {
              const next = Number.parseInt(event.target.value, 10);
              setTargetN(Number.isSafeInteger(next) ? Math.min(200, Math.max(1, next)) : 1);
            }}
          />
          <p className="make__hint">{copy.studyTargetHint}</p>
        </div>
      </div>

      <div className="make__block">
        <span className="make__label label">{copy.studySpineLabel}</span>
        <p className="make__hint make__hint--above">{copy.studySpineHint}</p>
        <ul className="make__topics">
          {topics.map((topic, index) => (
            // Index is the identity here: these rows are positions in a spine, and
            // reordering one is exactly the same act as rewriting it.
            // eslint-disable-next-line react/no-array-index-key
            <li className="make__topic" key={index}>
              <span className="make__topic-n num label" aria-hidden="true">
                {index + 1}
              </span>
              <input
                className="input make__topic-title"
                type="text"
                value={topic.title}
                aria-label={`${copy.studyTopicTitlePlaceholder} ${index + 1}`}
                placeholder={copy.studyTopicTitlePlaceholder}
                onChange={(event) => setTopic(index, { title: event.target.value })}
              />
              <input
                className="input make__topic-intent"
                type="text"
                value={topic.intent}
                aria-label={copy.studyTopicIntentPlaceholder}
                placeholder={copy.studyTopicIntentPlaceholder}
                onChange={(event) => setTopic(index, { intent: event.target.value })}
              />
              {topics.length > 3 && (
                <button
                  className="make__drop label"
                  type="button"
                  onClick={() => setTopics((current) => current.filter((_, i) => i !== index))}
                >
                  {copy.studyTopicRemoveCta}
                </button>
              )}
            </li>
          ))}
        </ul>
        <div className="make__topic-acts">
          {topics.length < 6 && (
            <button
              className="make__add label"
              type="button"
              onClick={() => setTopics((current) => [...current, { title: "", intent: "" }])}
            >
              {copy.studyTopicAddCta}
            </button>
          )}
          <button className="make__add label" type="button" onClick={draft} disabled={working}>
            {copy.studySpineDraftCta}
          </button>
        </div>
      </div>

      <div className="make__block">
        <span className="make__label label">{copy.studyHypothesesLabel}</span>
        <p className="make__hint make__hint--above">{copy.studyHypothesesHint}</p>
        <ul className="make__topics">
          {hypotheses.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li className="make__topic" key={index}>
              <input
                className="input make__topic-intent"
                type="text"
                value={item}
                aria-label={`${copy.studyHypothesesLabel} ${index + 1}`}
                placeholder={copy.studyHypothesisPlaceholder}
                onChange={(event) => {
                  const value = event.target.value;
                  setHypotheses((current) =>
                    current.map((entry, i) => (i === index ? value : entry)),
                  );
                }}
              />
            </li>
          ))}
        </ul>
        <div className="make__topic-acts">
          <button
            className="make__add label"
            type="button"
            onClick={() => setHypotheses((current) => [...current, ""])}
          >
            {copy.studyHypothesisAddCta}
          </button>
        </div>
      </div>

      <div className="make__commit">
        <button className="btn btn--primary" type="submit" disabled={working}>
          {copy.studyCreateCta}
        </button>
        <button className="btn btn--text" type="button" onClick={onCancel}>
          {copy.studyCancelCta}
        </button>
      </div>
    </form>
  );
}
